/* =========================================================
   Bestcold — global theme JS
   ---------------------------------------------------------
   Kept intentionally small and modular for Milestone 1: mobile
   nav, sticky/transparent header state, product gallery swap,
   and variant→price/availability sync. Every module re-runs on
   `shopify:section:load` so editing a section's settings in the
   Theme Editor doesn't leave stale/dead bindings behind, and
   every module is a no-op if its markup isn't present on the
   page (so this one file can safely load site-wide).
   ========================================================= */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var mount = event.target.querySelector && event.target.querySelector('[data-hero-parallax]');
    if (mount) teardownHeroParallax(mount);
  });

  function initAll(root) {
    initMobileNav(root);
    initHeaderScrollState();
    initProductGallery(root);
    initVariantSelector(root);
    initHeroParallax(root);
    initCarousel(root);
    initQuantityStepper(root);
    initProductTabs(root);
    initReveal(root);
  }

  /* ---------------------------------------------------------
     Scroll reveal (V2). The [data-animate] / [data-animate-group]
     hooks already existed in the markup from V1 but nothing ever
     acted on them; this is that missing half.

     Safety: the "hidden" starting state lives behind the
     `.reveal-ready` class, which ONLY this function adds. So if
     the script fails to run — old browser, JS error, blocked
     file — nothing is ever hidden, and the page renders exactly
     as it does today. Visitors who prefer reduced motion skip
     the whole mechanism and get the content immediately.
     --------------------------------------------------------- */
  var revealObserver = null;
  function initReveal(root) {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('reveal-ready');

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target); // reveal once, then stop watching
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }

    var targets = root.querySelectorAll ? root.querySelectorAll('[data-animate]:not([data-reveal-bound])') : [];
    targets.forEach(function (el) {
      el.setAttribute('data-reveal-bound', 'true');

      // Items inside a group cascade instead of all landing at once.
      var group = el.closest('[data-animate-group]');
      if (group) {
        var siblings = Array.prototype.slice.call(group.querySelectorAll('[data-animate]'));
        var i = siblings.indexOf(el);
        if (i > 0) el.style.transitionDelay = Math.min(i * 70, 350) + 'ms';
      }

      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------
     Generic snap-carousel dots (V2). Enhancement only: the
     track is a CSS scroll-snap row that already works by
     swiping with JS off — this just adds position dots, and
     only when the track actually overflows (i.e. on phones).
     Used by the homepage product selector; any section can opt
     in with data-carousel / data-carousel-track /
     data-carousel-dots.
     --------------------------------------------------------- */
  function initCarousel(root) {
    var carousels = root.querySelectorAll ? root.querySelectorAll('[data-carousel]:not([data-bound])') : [];
    carousels.forEach(function (carousel) {
      carousel.setAttribute('data-bound', 'true');

      var track = carousel.querySelector('[data-carousel-track]');
      var dotsWrap = carousel.querySelector('[data-carousel-dots]');
      if (!track || !dotsWrap) return;

      var cells = Array.prototype.slice.call(track.children);
      if (cells.length < 2) return;
      var dots = [];

      function offsetWithin(cell) {
        var r = cell.getBoundingClientRect();
        var t = track.getBoundingClientRect();
        var pad = parseFloat(window.getComputedStyle(track).paddingLeft) || 0;
        return track.scrollLeft + (r.left - t.left) - pad;
      }

      function buildDots() {
        dotsWrap.textContent = '';
        dots = cells.map(function (cell, i) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', String(i + 1));
          dot.addEventListener('click', function () {
            // Deliberately an instant scrollLeft assignment. Both
            // scrollTo({behavior:'smooth'}) and CSS `scroll-behavior:
            // smooth` turned out to be silent no-ops on this scroll-snap
            // track during testing — the dots looked wired up but never
            // moved anything. An instant jump to the next card is honest,
            // works everywhere, and is what a snap carousel does on swipe
            // anyway.
            track.scrollLeft = offsetWithin(cell);
            // Update the dots straight away instead of waiting for the
            // scroll handler: that one is rAF-throttled, and rAF is frozen
            // whenever the page isn't visible (background tab, low-power
            // mode), which would leave the dots pointing at the wrong card.
            syncActive();
          });
          dotsWrap.appendChild(dot);
          return dot;
        });
      }

      function syncActive() {
        if (!dots.length) return;
        var t = track.getBoundingClientRect();
        var mid = t.left + t.width / 2;
        var best = 0;
        var bestDist = Infinity;
        cells.forEach(function (cell, i) {
          var r = cell.getBoundingClientRect();
          var dist = Math.abs(r.left + r.width / 2 - mid);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === best);
          dot.setAttribute('aria-selected', i === best ? 'true' : 'false');
        });
      }

      function refresh() {
        var overflows = track.scrollWidth - track.clientWidth > 8;
        dotsWrap.hidden = !overflows;
        if (!overflows) return;
        if (!dots.length) buildDots();
        syncActive();
      }

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { syncActive(); ticking = false; });
      }, { passive: true });

      // The Theme Editor can swap this section out from under us; bail (and
      // stop doing work) once our track is no longer in the document, rather
      // than leaving a live resize handler behind on a detached node.
      var resizeTimer;
      window.addEventListener('resize', function () {
        if (!document.body.contains(track)) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(refresh, 150);
      });

      refresh();
    });
  }

  /* ---------------------------------------------------------
     Quantity stepper (V2): − / + buttons around the native
     number input on the product page. The input itself is
     untouched and still posts normally, so this is purely a
     nicer hit target; with JS off the number input remains.
     --------------------------------------------------------- */
  function initQuantityStepper(root) {
    var steppers = root.querySelectorAll ? root.querySelectorAll('[data-qty-stepper]:not([data-bound])') : [];
    steppers.forEach(function (stepper) {
      stepper.setAttribute('data-bound', 'true');
      var input = stepper.querySelector('input[type="number"]');
      if (!input) return;

      stepper.querySelectorAll('[data-qty-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var step = Number(btn.getAttribute('data-qty-step')) || 1;
          var min = Number(input.getAttribute('min') || 1);
          var next = Math.max(min, (Number(input.value) || min) + step);
          input.value = next;
          // Let anything listening (cart JS, validation) see a real change.
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------------------------------------------------------
     Product section tabs (V2). Progressive enhancement over
     stacked content: without JS every panel is simply visible
     one after another (that's the server-rendered state), and
     this turns them into a tab strip. Tabs scroll horizontally
     on phones rather than wrapping into a tall block.
     --------------------------------------------------------- */
  function initProductTabs(root) {
    var groups = root.querySelectorAll ? root.querySelectorAll('[data-tabs]:not([data-bound])') : [];
    groups.forEach(function (group) {
      group.setAttribute('data-bound', 'true');

      var tablist = group.querySelector('[data-tabs-list]');
      var panels = Array.prototype.slice.call(group.querySelectorAll('[data-tab-panel]'));
      if (!tablist || panels.length < 2) return;

      var tabs = panels.map(function (panel, i) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'product-tabs__tab';
        tab.textContent = panel.getAttribute('data-tab-label') || 'Sectie ' + (i + 1);
        tab.setAttribute('role', 'tab');
        tab.id = 'tab-' + (panel.id || i);
        tab.setAttribute('aria-controls', panel.id);
        tab.addEventListener('click', function () { select(i); });
        tab.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          select(next);
          tabs[next].focus();
        });
        tablist.appendChild(tab);
        return tab;
      });

      function select(index) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.classList.toggle('is-active', on);
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
        });
      }

      group.classList.add('product-tabs--enhanced');
      tablist.hidden = false;
      select(0);
    });
  }

  /* ---------------------------------------------------------
     Mobile nav: hamburger toggle + overlay + Escape to close.
     --------------------------------------------------------- */
  function initMobileNav(root) {
    var toggles = root.querySelectorAll ? root.querySelectorAll('[data-mobile-nav-toggle]:not([data-bound])') : [];
    if (!toggles.length) return;

    var nav = document.getElementById('MobileNav');
    if (!nav) return;

    toggles.forEach(function (toggle) {
      toggle.setAttribute('data-bound', 'true');
      toggle.addEventListener('click', function () {
        var isOpen = nav.classList.contains('is-open');
        setMobileNavOpen(!isOpen);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('is-open')) {
        setMobileNavOpen(false);
      }
    });

    function setMobileNavOpen(open) {
      nav.classList.toggle('is-open', open);
      nav.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.querySelectorAll('.mobile-nav__overlay').forEach(function (overlay) {
        overlay.classList.toggle('is-open', open);
      });
      document.querySelectorAll('[data-mobile-nav-toggle]').forEach(function (t) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.body.style.overflow = open ? 'hidden' : '';
    }
  }

  /* ---------------------------------------------------------
     Header: adds `.site-header--scrolled` past a small
     threshold so the transparent-over-hero header (Milestone 2)
     becomes solid/sticky once the visitor scrolls, and stays
     solid on every non-transparent page regardless.
     --------------------------------------------------------- */
  var headerScrollBound = false;
  function initHeaderScrollState() {
    var header = document.querySelector('[data-header]');
    if (!header || headerScrollBound) return;
    headerScrollBound = true;

    var threshold = 40;
    var ticking = false;

    function update() {
      header.classList.toggle('site-header--scrolled', window.scrollY > threshold);
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------------
     Product page gallery: click a thumbnail to swap the main
     image. Pure DOM swap, no dependency, works without JS too
     (all images exist in the markup; only the extra ones start
     `hidden`).
     --------------------------------------------------------- */
  function initProductGallery(root) {
    var galleries = root.querySelectorAll ? root.querySelectorAll('[data-product-gallery]:not([data-bound])') : [];
    galleries.forEach(function (gallery) {
      gallery.setAttribute('data-bound', 'true');
      var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      var images = gallery.querySelectorAll('[data-gallery-image]');
      if (!thumbs.length || !images.length) return;

      // Single source of truth for "which image is showing", so the
      // thumbnails and the V2 prev/next arrows can't drift apart.
      var current = 0;

      function show(index) {
        var count = images.length;
        current = ((index % count) + count) % count; // wraps both ways
        images.forEach(function (img) {
          var match = Number(img.getAttribute('data-index')) === current;
          img.hidden = !match;
          img.classList.toggle('is-active', match);
        });
        thumbs.forEach(function (t) {
          t.classList.toggle('is-active', Number(t.getAttribute('data-index')) === current);
        });
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          show(Number(thumb.getAttribute('data-index')));
        });
      });

      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', function () { show(current - 1); });
      if (next) next.addEventListener('click', function () { show(current + 1); });
    });
  }

  /* ---------------------------------------------------------
     Variant selector: keeps the hidden `id` input (and the Add
     to cart button's disabled state) in sync with the option
     selects, using the product's variants JSON already inlined
     in the page — no extra network request.
     Full price/media swap on variant change lands with the
     Milestone 4 product template; this covers correct add-to-
     cart behavior today.
     --------------------------------------------------------- */
  function initVariantSelector(root) {
    var forms = root.querySelectorAll ? root.querySelectorAll('.product-page__form:not([data-bound])') : [];
    forms.forEach(function (form) {
      form.setAttribute('data-bound', 'true');
      var variantsScript = form.querySelector('[data-product-variants]');
      var idInput = form.querySelector('[data-variant-id-input]');
      var selects = form.querySelectorAll('[data-option-selector]');
      var submitBtn = form.querySelector('[data-add-to-cart]');
      if (!variantsScript || !idInput || !selects.length) return;

      var variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (err) {
        return;
      }

      function currentOptions() {
        var options = [];
        selects.forEach(function (select) {
          options[Number(select.getAttribute('data-option-index'))] = select.value;
        });
        return options;
      }

      function findVariant(options) {
        return variants.filter(function (variant) {
          return options.every(function (value, index) {
            return variant.options[index] === value;
          });
        })[0];
      }

      function onChange() {
        var match = findVariant(currentOptions());
        if (!match) return;
        idInput.value = match.id;
        if (submitBtn) {
          submitBtn.disabled = !match.available;
          submitBtn.textContent = match.available
            ? submitBtn.getAttribute('data-label-available') || submitBtn.textContent
            : submitBtn.getAttribute('data-label-sold-out') || submitBtn.textContent;
        }
      }

      selects.forEach(function (select) {
        select.addEventListener('change', onChange);
      });
    });
  }

  /* ---------------------------------------------------------
     Hero 2.5D parallax: subtle pointer + scroll driven translate
     on the product image layer. Deliberately CSS-transform-only
     (no WebGL/canvas) — the source is a single flat product
     cutout, so there's no 3D geometry for a real 3D engine to add
     value to yet. Same guardrails as TUAI's theme-3d.js: desktop
     only, off when the visitor prefers reduced motion, paused via
     IntersectionObserver while off-screen, torn down (not just
     left dangling) on `shopify:section:unload` so the Theme
     Editor reloading the Hero section never doubles-up window
     scroll/resize listeners.
     --------------------------------------------------------- */
  var MOBILE_BREAKPOINT = 900;
  var parallaxTeardownByMount = new WeakMap();

  function initHeroParallax(root) {
    var mounts = root.querySelectorAll ? root.querySelectorAll('[data-hero-parallax]:not([data-bound])') : [];
    mounts.forEach(function (mount) {
      mount.setAttribute('data-bound', 'true');

      var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
      if (prefersReducedMotion || !isDesktop) return; // leave the static image as-is

      var layer = mount.querySelector('[data-parallax-layer="product"]');
      var hero = mount.closest('.hero');

      // V2: the airflow gets its own, slower layer. Two layers moving at
      // different rates is what actually sells the depth — the unit leads,
      // the air trails behind it.
      // V3: the cinematic mode has no separate product layer — the whole
      // photographic plate gets a very small drift instead (scene layer,
      // scaled slightly so its edges never show), with the air overlay
      // trailing at its own rate. Every layer is optional and guarded.
      var airLayer = mount.querySelector('[data-parallax-layer="airflow"]');
      var sceneLayer = mount.querySelector('[data-parallax-layer="scene"]');
      var AIR_RATE = 0.45;
      var SCENE_RATE = 0.22;
      var scenePlate = sceneLayer ? sceneLayer.querySelector('.hero__plate') : null;
      if ((!layer && !scenePlate) || !hero) return;

      var pointer = { x: 0, y: 0 };
      var scrollOffset = 0;
      var isVisible = true;
      var rafId = null;

      function onPointerMove(e) {
        var rect = hero.getBoundingClientRect();
        pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      }

      function onScroll() {
        var rect = hero.getBoundingClientRect();
        // 0 when hero top is at viewport top, grows as it scrolls upward.
        scrollOffset = Math.max(0, -rect.top);
      }

      function render() {
        rafId = requestAnimationFrame(render);
        if (!isVisible || document.hidden) return;
        var px = pointer.x * 10; // max ~10px pointer drift
        var py = pointer.y * 8;
        var sy = Math.min(scrollOffset * 0.06, 24); // gentle upward drift, capped
        if (layer) {
          layer.style.transform = 'translate3d(' + px.toFixed(1) + 'px, ' + (py - sy).toFixed(1) + 'px, 0)';
        }
        if (scenePlate) {
          // scale(1.05) buys the drift room: the plate always overshoots the
          // viewport, so its edges never peek in while it moves.
          scenePlate.style.transform =
            'scale(1.05) translate3d(' + (px * SCENE_RATE).toFixed(1) + 'px, ' + ((py - sy) * SCENE_RATE).toFixed(1) + 'px, 0)';
        }
        if (airLayer) {
          airLayer.style.transform =
            'translate3d(' + (px * AIR_RATE).toFixed(1) + 'px, ' + ((py - sy) * AIR_RATE).toFixed(1) + 'px, 0)';
        }
      }

      var observer = new IntersectionObserver(function (entries) {
        isVisible = entries[0].isIntersecting;
      }, { threshold: 0 });
      observer.observe(hero);

      function onVisibilityChange() {
        isVisible = isVisible && !document.hidden;
      }

      var resizeTimeout;
      function onResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function () {
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            teardownHeroParallax(mount);
          }
        }, 150);
      }

      hero.addEventListener('pointermove', onPointerMove);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      document.addEventListener('visibilitychange', onVisibilityChange);
      render();

      parallaxTeardownByMount.set(mount, function () {
        cancelAnimationFrame(rafId);
        observer.disconnect();
        hero.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (layer) layer.style.transform = '';
        if (scenePlate) scenePlate.style.transform = '';
        if (airLayer) airLayer.style.transform = '';
      });
    });
  }

  function teardownHeroParallax(mount) {
    var fn = parallaxTeardownByMount.get(mount);
    if (fn) {
      fn();
      parallaxTeardownByMount.delete(mount);
    }
    mount.removeAttribute('data-bound');
  }
})();
